import { useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-building-line',
    title: 'Escritório que vende o sonho antes de mostrar o imóvel',
    description: 'O cliente que entra na imobiliária já está comprando com os olhos. Um ambiente sofisticado, organizado e acolhedor cria a percepção de que a empresa cuida dos detalhes — e isso se transfere para a confiança no negócio.',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Sala de atendimento que fecha negócios de alto valor',
    description: 'Uma mesa de reunião imponente, cadeiras confortáveis e um ambiente privativo criam as condições ideais para o cliente tomar a decisão de compra. O ambiente certo reduz objeções e acelera o fechamento.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito da imobiliária completa',
    description: 'Recepção, sala de atendimento, área dos corretores e sala de reunião projetados em 3D. Você aprova o layout antes de fabricar — e garante que cada ambiente transmita o padrão da sua imobiliária.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem fora do horário de atendimento',
    description: 'Nossa equipe instala nos horários que não atrapalham o atendimento aos clientes. Sua imobiliária não perde um dia de negócios durante a reforma.',
  },
];

const blocks = [
  {
    title: 'Recepção que Cria a Primeira Impressão do Sonho',
    text: [
      'O cliente que entra na imobiliária está prestes a tomar uma das maiores decisões financeiras da vida. A recepção precisa transmitir segurança, sofisticação e confiança — os mesmos valores que ele espera da empresa que vai intermediar esse negócio. Uma recepção bem projetada não é custo, é investimento em conversão.',
      'Nossos projetos de recepção para imobiliárias incluem balcão com tampo em granito ou laminado premium, frente em MDF com o logo da imobiliária em relevo ou iluminado, cadeiras de espera em courvin ou tecido de alta qualidade com estrutura em madeira ou metal, mesa de centro com tampo em vidro temperado, painel de TV para exibição de imóveis disponíveis, e iluminação LED que cria um ambiente acolhedor e sofisticado.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20real%20estate%20agency%20reception%20area%20with%20elegant%20counter%2C%20comfortable%20waiting%20chairs%2C%20property%20listings%20on%20screen%2C%20warm%20sophisticated%20lighting%2C%20premium%20interior%20design%2C%20trust%20and%20luxury%20atmosphere%2C%20professional%20photography&width=800&height=500&seq=imob_v1_1&orientation=landscape',
    reverse: false,
  },
  {
    title: 'Sala de Atendimento que Fecha Negócios de Alto Valor',
    text: [
      'A sala de atendimento é onde o corretor apresenta os imóveis, negocia condições e fecha o contrato. Ela precisa ser privativa, confortável e transmitir a seriedade do negócio. Um cliente que se sente bem no ambiente tem menos objeções e toma a decisão de compra com mais confiança.',
      'Projetamos salas de atendimento para imobiliárias com mesa de reunião para 4 a 6 pessoas em MDF com tampo em granito ou laminado premium, cadeiras executivas em courvin ou tecido de alta qualidade, painel de TV com suporte articulado para apresentação de imóveis em alta resolução, iluminação LED regulável que adapta o ambiente para diferentes momentos da negociação, e divisórias em vidro temperado que criam privacidade sem isolar visualmente o espaço.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20real%20estate%20office%20meeting%20room%20with%20conference%20table%2C%20executive%20chairs%2C%20TV%20panel%20showing%20property%2C%20warm%20sophisticated%20lighting%2C%20premium%20interior%20design%2C%20professional%20negotiation%20space&width=800&height=500&seq=imob_v1_2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Área dos Corretores e Sala de Reunião Interna Completas',
    text: [
      'Os corretores passam horas no escritório prospectando clientes, preparando propostas e acompanhando negociações. Uma área de trabalho bem projetada — com estações organizadas, cadeiras ergonômicas e espaço para reuniões rápidas — aumenta a produtividade da equipe e reduz o tempo de fechamento de cada negócio.',
      'Para a área dos corretores, projetamos estações de trabalho com mesas em MDF de alta espessura, divisórias que criam privacidade sem isolar, cadeiras ergonômicas com regulagem de altura e apoio lombar, e armários individuais para cada corretor guardar documentos e materiais. Para a sala de reunião interna, mesa para 8 a 10 pessoas, cadeiras executivas, painel de TV e iluminação adequada para apresentações e treinamentos.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20real%20estate%20office%20open%20workspace%20with%20organized%20desks%2C%20ergonomic%20chairs%2C%20professional%20environment%2C%20warm%20lighting%2C%20productive%20team%20workspace%2C%20neutral%20tones%2C%20premium%20interior%20design&width=800&height=500&seq=imob_v1_3&orientation=landscape',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: 'https://readdy.ai/api/search-image?query=Modern%20real%20estate%20agency%20reception%20with%20elegant%20counter%2C%20warm%20lighting%2C%20sophisticated%20design%2C%20property%20listings%20display&width=600&height=400&seq=imob_g1&orientation=landscape',
    alt: 'Recepção sofisticada de imobiliária',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Real%20estate%20office%20meeting%20room%20with%20conference%20table%2C%20executive%20chairs%2C%20TV%20panel%2C%20warm%20sophisticated%20lighting&width=600&height=400&seq=imob_g2&orientation=landscape',
    alt: 'Sala de atendimento com mesa de reunião',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Real%20estate%20office%20workstations%20with%20organized%20desks%2C%20ergonomic%20chairs%2C%20professional%20environment%2C%20warm%20lighting&width=600&height=400&seq=imob_g3&orientation=landscape',
    alt: 'Área dos corretores com estações organizadas',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Real%20estate%20agency%20internal%20conference%20room%20with%20large%20table%2C%20chairs%2C%20presentation%20screen%2C%20professional%20design&width=600&height=400&seq=imob_g4&orientation=landscape',
    alt: 'Sala de reunião interna para treinamentos',
  },
];

const WA_LINK = 'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Imobili%C3%A1rias%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!';

export default function ImobiliariasPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Imobiliária Caruaru PE | Escritório Premium – Home Office Premium',
    description: 'Mobiliário para imobiliárias em Caruaru PE. Recepção, sala de atendimento, área dos corretores e sala de reunião. Projeto 3D gratuito e montagem fora do horário de atendimento. Atendemos imobiliárias no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário imobiliária Recife, móveis corretor imóveis Petrolina, sala atendimento imobiliária Garanhuns, recepção imobiliária Caruaru, mobiliário escritório imóveis Agreste PE, mesa reunião imobiliária Pernambuco, cadeira corretor Nordeste, balcão imobiliária Recife, mobiliário imobiliária Caruaru',
    path: '/imobiliarias',
  });

  useSchemaLD({
    id: 'schema-imobiliarias',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Imobiliárias | Caruaru PE',
      description: 'Recepção, sala de atendimento, área dos corretores e sala de reunião para imobiliárias. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/imobiliarias`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Imobiliárias',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20premium%20real%20estate%20agency%20office%20interior%20with%20elegant%20reception%2C%20sophisticated%20design%2C%20warm%20lighting%2C%20luxury%20atmosphere%2C%20professional%20property%20business%20environment%2C%20wide%20angle%20photography&width=1400&height=700&seq=imob_hero&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Imobiliárias · Corretoras de Imóveis · Incorporadoras
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Escritório que vende o sonho antes de mostrar o primeiro imóvel.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Recepção sofisticada, sala de atendimento que fecha negócios e área dos corretores que aumenta a produtividade. Porque o ambiente da imobiliária é parte do produto.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Montagem fora do horário de atendimento · Suporte local em Caruaru e Agreste
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que imobiliárias de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Imobiliária</span> que Convertem Visitas em Negócios Fechados
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja sua imobiliária completa em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Recepção, sala de atendimento, área dos corretores e sala de reunião projetados em 3D. Você aprova cada detalhe antes de fabricarmos. Montagem fora do horário de atendimento.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Ambientes de <span className="text-[#E8610A]">Imobiliária Premium</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
